import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { GrLinkTop } from "react-icons/gr";
import './Humanizer.css';

import { HUMANIZER_API } from '../apiConfig';

// CSS references similar to plagiarism.js
import '../Css/BeamProperties.css';
import '../Css/BeamDeflection.css';
import '../Css/NumberLine.css';
import '../Css/AboutUS.css';
import '../Css/Navbar.css';
import '../Css/Padeye.css';

// ── Small helpers ──────────────────────────────────────────────────────────
const wordCount = (text) => (text.trim() ? text.trim().split(/\s+/).length : 0);
const charCount = (text) => text.length;

const sentimentLabel = (val) => {
  if (val > 0.3) return { label: 'Positive 😊', color: '#10b981' };
  if (val < -0.3) return { label: 'Negative 😟', color: '#ef4444' };
  return { label: 'Neutral 😐', color: '#a78bfa' };
};

const aiScoreColor = (score) => {
  if (score >= 70) return '#ef4444';
  if (score >= 40) return '#f59e0b';
  return '#10b981';
};

// ── MetricCard component ───────────────────────────────────────────────────
const MetricCard = ({ title, icon, before, after, unit = '', lowerBetter = false, single, singleColor }) => {
  const improved = lowerBetter ? after < before : after > before;
  const diff = after !== undefined ? Math.abs(after - before) : null;

  return (
    <div className="metric-card">
      <div className="metric-icon">{icon}</div>
      <div className="metric-title">{title}</div>
      {single !== undefined ? (
        <div className="metric-single" style={{ color: singleColor || '#a78bfa' }}>{single}</div>
      ) : (
        <div className="metric-values">
          <span className="metric-before">{before}{unit}</span>
          <span className="metric-arrow">→</span>
          <span className="metric-after" style={{ color: improved ? '#10b981' : '#ef4444' }}>
            {after}{unit}
          </span>
          {diff > 0 && (
            <span className="metric-diff" style={{ color: improved ? '#10b981' : '#ef4444' }}>
              {improved ? '▲' : '▼'} {diff}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ── LoadingDots ────────────────────────────────────────────────────────────
const LoadingDots = () => (
  <div className="loading-dots">
    <span /><span /><span />
  </div>
);

// ── Main App ───────────────────────────────────────────────────────────────
export default function HumanizerUI() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [strength, setStrength] = useState('medium');
  const [analysis, setAnalysis] = useState(null);
  const [copied, setCopied] = useState(false);

  const [ballCount, setBallCount] = useState(0);
  const ballRefs = useRef([]);

  const words = useMemo(() => wordCount(input), [input]);
  const chars = useMemo(() => charCount(input), [input]);

  // Ball count calculator for background animation
  useEffect(() => {
    const spacingVH = 70;
    const calculateBallCount = () => {
      const pageHeight = document.documentElement.scrollHeight;
      const vh = window.innerHeight / 100;
      const spacingPx = spacingVH * vh;
      const count = Math.ceil(pageHeight / spacingPx);
      setBallCount(count);
    };

    calculateBallCount();
    window.addEventListener('resize', calculateBallCount);
    window.addEventListener('scroll', calculateBallCount);

    const observer = new MutationObserver(calculateBallCount);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      window.removeEventListener('resize', calculateBallCount);
      window.removeEventListener('scroll', calculateBallCount);
      observer.disconnect();
    };
  }, []);

  // Intersection observer for animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    ballRefs.current.forEach(ball => ball && observer.observe(ball));
    return () => observer.disconnect();
  }, [ballCount]);

  const humanize = useCallback(async () => {
    if (!input.trim()) return;
    setLoading(true);
    setError('');
    setAnalysis(null);
    setOutput('');

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${HUMANIZER_API}/api/humanize`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ text: input, strength }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setOutput(data.humanized);
        setAnalysis({ before: data.analysis_before, after: data.analysis_after });
      } else {
        if (res.status === 429) {
          setError("You have reached your 8 free uses for today! Please Log In or Sign Up to unlock unlimited access.");
        } else {
          setError(data.error || 'Humanization failed or engine is warming up.');
        }
      }
    } catch (err) {
      console.error("Humanization error:", err);
      setError('Cannot connect to neural engine. Please check your internet or try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [input, strength]);

  const clear = () => {
    setInput('');
    setOutput('');
    setError('');
    setAnalysis(null);
    setCopied(false);
  };

  const copyToClipboard = () => {
    if (!output) return;
    navigator.clipboard.writeText(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const strengthOptions = [
    { id: 'light', label: '🪶 Light', desc: 'Subtle tweaks' },
    { id: 'medium', label: '⚖️ Medium', desc: 'Balanced rewrite' },
    { id: 'strong', label: '🔥 Strong', desc: 'Deep transform' },
  ];

  return (
    <>
      <Helmet>
        <title>AI Text Humanizer – OOK | Undetectable AI Content</title>
        <meta
          name="description"
          content="Convert sterile AI-generated text into natural, engaging human writing with the OOK AI Humanizer. Our tool restructures machine-output to bypass even the most advanced AI detectors while maintaining your unique intent and emotional resonance. Achieve undetectable, high-quality content."
        />
        <meta name="keywords" content="AI humanizer, undetectable AI, bypass AI detectors, humanize AI text, rewrite AI content, OOK Calculator" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="AI Text Humanizer – OOK | Undetectable AI Content" />
        <meta property="og:description" content="Convert sterile AI-generated text into natural, engaging human writing. Bypasses advanced AI detection." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.ook-calculator.com/Humanizer" />

        {/* Structured schemas */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "OOK AI Humanizer",
            "description": "Convert AI-generated text into natural human writing. Restructures machine-output to bypass AI detectors.",
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "Any",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
          }
          `}
        </script>
      </Helmet>

      <div className="humanizer-theme-wrapper">
        {/* BREADCRUMB */}
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <a href="https://ook-calculator.com">Home</a>
          <span aria-hidden="true">›</span>
          <span aria-current="page">AI Humanizer</span>
        </nav>

        {/* HERO */}
        <header className="hero" role="banner">
          <div className="badge" aria-label="Tool category">Free Online Tool</div>
          <h1>
            Transform AI Text Into<br />— <em>Natural Human Writing</em>
          </h1>
          <p className="hero-sub">
            Paste any AI-generated content and get back writing that reads, flows, and feels genuinely human. No detection. No flags.
          </p>
          <div className="trust-row" aria-label="Tool highlights">
            <span className="trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              Light, Medium & Strong strengths
            </span>
            <span className="trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              Zero data retention
            </span>
            <span className="trust-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              Bypass advanced AI detectors
            </span>
          </div>
        </header>

        {/* BACKGROUND BALL ANIMATION */}
        <div className="ball-section">
          {Array.from({ length: ballCount }, (_, i) => (
            <div
              key={i}
              ref={el => (ballRefs.current[i] = el)}
              className={`ball ball-${(i % 10) + 1}`}
              style={{
                position: 'absolute',
                top: `${i * 70}vh`,
                left: i % 2 === 0 ? '0%' : '97%',
                transform: 'translateX(-50%)',
                animationDelay: `${Math.random() * 5}s`,
              }}
            />
          ))}
        </div>

        {/* TOOL WIDGET */}
        <main id="main-content">
          <section className="tool-section" aria-label="AI Humanizer tool">
            <div className="mode-tabs" role="tablist" aria-label="Humanization strength">
              {strengthOptions.map(({ id, label, desc }) => (
                <button
                  key={id}
                  id={`strength-${id}`}
                  className={`tab ${strength === id ? 'active' : ''}`}
                  role="tab"
                  aria-selected={strength === id}
                  onClick={() => setStrength(id)}
                  title={desc}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="editor-grid">
              {/* INPUT PANE */}
              <div className="editor-pane">
                <div className="pane-header">
                  <span><span className="icon">✏</span> &nbsp;Original Text</span>
                  <span className="word-count" id="wc">
                    {words} words · {chars} chars
                  </span>
                </div>
                <textarea
                  id="input-textarea"
                  placeholder="Paste your AI-generated text here..."
                  aria-label="Input text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={loading}
                />
                <div className="pane-footer">
                  <button className="btn-sm" onClick={clear} disabled={loading}>Clear</button>
                  <button
                    className="btn-sm primary"
                    id="humanize-btn"
                    onClick={humanize}
                    disabled={loading || !input.trim()}
                  >
                    {loading ? (
                      <span className="loading-spinner"></span>
                    ) : (
                      'Humanize Text →'
                    )}
                  </button>
                </div>
              </div>

              {/* OUTPUT PANE */}
              <div className="editor-pane">
                <div className="pane-header">
                  <span><span className="icon">⚡</span> &nbsp;Humanized Text</span>
                  <span className="word-count">
                    {output ? `${wordCount(output)} words` : '—'}
                  </span>
                </div>
                <div className="output-area" id="outputArea" aria-live="polite">
                  {loading ? (
                    <div className="output-loading">
                      <LoadingDots />
                      <p>Humanizing your text...</p>
                    </div>
                  ) : output ? (
                    <div className="output-text">{output}</div>
                  ) : (
                    <div className="standby" id="standbyMsg">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <rect x="4" y="11" width="3" height="6" rx="1.5" fill="#ccc" />
                        <rect x="9.5" y="7" width="3" height="14" rx="1.5" fill="#ccc" />
                        <rect x="15" y="9" width="3" height="10" rx="1.5" fill="#ccc" />
                        <rect x="20.5" y="12" width="3" height="4" rx="1.5" fill="#ccc" />
                      </svg>
                      Neural engine on standby.<br />Awaiting input.
                    </div>
                  )}
                </div>
                <div className="pane-footer">
                  <span></span>
                  <div className="pane-actions">
                    <button
                      className="btn-sm"
                      id="copy-btn"
                      onClick={copyToClipboard}
                      disabled={!output || loading}
                    >
                      {copied ? '✅ Copied!' : '📋 Copy'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ERROR */}
            {error && (
              <div className="error-banner" id="error-banner" style={{ marginTop: '20px' }}>
                ❌ {error}
              </div>
            )}

            {/* ── ANALYSIS PANEL ── */}
            {analysis && (
              <section className="analysis-section" id="analysis-panel">
                <h2 className="analysis-title">📊 Text Analysis</h2>
                <div className="metrics-grid">
                  <MetricCard
                    title="AI Detection Score"
                    icon="🤖"
                    before={analysis.before.ai_score}
                    after={analysis.after.ai_score}
                    unit="%"
                    lowerBetter
                  />
                  <MetricCard
                    title="Readability"
                    icon="📖"
                    before={analysis.before.readability}
                    after={analysis.after.readability}
                    unit="/100"
                  />
                  <MetricCard
                    title="Formality"
                    icon="🎩"
                    before={analysis.before.formality}
                    after={analysis.after.formality}
                    unit="%"
                    lowerBetter
                  />
                  <MetricCard
                    title="Sentiment"
                    icon="💬"
                    single={sentimentLabel(analysis.before.sentiment).label}
                    singleColor={sentimentLabel(analysis.before.sentiment).color}
                  />
                </div>

                {/* AI Score bar */}
                <div className="ai-score-bar-wrap">
                  <div className="ai-score-row">
                    <span>AI Score Before</span>
                    <strong style={{ color: aiScoreColor(analysis.before.ai_score) }}>
                      {analysis.before.ai_score}%
                    </strong>
                  </div>
                  <div className="progress-track">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${analysis.before.ai_score}%`,
                        background: aiScoreColor(analysis.before.ai_score),
                      }}
                    />
                  </div>
                  <div className="ai-score-row" style={{ marginTop: 8 }}>
                    <span>AI Score After</span>
                    <strong style={{ color: aiScoreColor(analysis.after.ai_score) }}>
                      {analysis.after.ai_score}%
                    </strong>
                  </div>
                  <div className="progress-track">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${analysis.after.ai_score}%`,
                        background: aiScoreColor(analysis.after.ai_score),
                      }}
                    />
                  </div>
                </div>
              </section>
            )}
          </section>

          {/* STATS */}
          <div className="stats-grid" role="list" aria-label="Usage statistics">
            <div className="stat-cell" role="listitem">
              <div className="stat-num">1M+</div>
              <div className="stat-label">Texts Humanized</div>
            </div>
            <div className="stat-cell" role="listitem">
              <div className="stat-num">99.4%</div>
              <div className="stat-label">Bypass Rate</div>
            </div>
            <div className="stat-cell" role="listitem">
              <div className="stat-num">3 Levels</div>
              <div className="stat-label">Humanization Strength</div>
            </div>
            <div className="stat-cell" role="listitem">
              <div className="stat-num">&lt;5s</div>
              <div className="stat-label">Average Processing Time</div>
            </div>
          </div>

          {/* WRITING MODES DESCRIPTION */}
          <section className="content-wrap section" id="modes">
            <p className="section-eyebrow">Humanization Strengths</p>
            <h2 className="section-title">Choose your <em>strength level</em></h2>
            <div className="modes-grid">
              <div className="mode-card">
                <div className="mode-label">Light</div>
                <h3>Subtle Tweaks</h3>
                <p>Gentle restructuring and word choice replacements. Ideal for preserving most of your phrasing while introducing slight human variation.</p>
              </div>
              <div className="mode-card highlighted">
                <div className="mode-label">Medium</div>
                <h3>Balanced Rewrite</h3>
                <p>Standard balanced transformation that maintains your intent while fully rephrasing sentences to defeat common detectors.</p>
              </div>
              <div className="mode-card">
                <div className="mode-label">Strong</div>
                <h3>Deep Transform</h3>
                <p>Complete structural shift. Thoroughly refactors text layouts, rhythms, and vocabulary for robust undetectable content.</p>
              </div>
            </div>
          </section>

          {/* HOW TO USE */}
          <section className="content-wrap section" id="steps">
            <p className="section-eyebrow">Quick Guide</p>
            <h2 className="section-title">3 Easy Steps to <em>Humanize AI Text</em></h2>
            <div className="modes-grid">
              <div className="mode-card">
                <div className="mode-label">Step 1</div>
                <h3>Paste Your AI Content</h3>
                <p>Copy text generated by ChatGPT, Claude, Gemini or another AI-writing tool, and paste the text in the input box.</p>
              </div>
              <div className="mode-card">
                <div className="mode-label">Step 2</div>
                <h3>Click on "Humanize"</h3>
                <p>With our AI Humanizer, your content is re-cast instantly in human language patterns with variations in tone, rhythm, vocabulary and sentence structure.</p>
              </div>
              <div className="mode-card">
                <div className="mode-label">Step 3</div>
                <h3>Copy and Use</h3>
                <p>Get back polished, human-sounding text in seconds. Use it in essays, blogs, emails, reports, or any content — with complete confidence.</p>
              </div>
            </div>
          </section>

          {/* WHAT IS / WHY CHOOSE */}
          <section className="content-wrap section" id="about">
            <p className="section-eyebrow">About the Tool</p>
            <h2 className="section-title">Why choose our <em>AI Humanizer</em>?</h2>
            <div className="features-grid">
              <article className="feature-card">
                <div className="feature-icon" aria-hidden="true">🤖</div>
                <div>
                  <h4>Bypass AI Detectors</h4>
                  <p>Our humanizer helps your text pass detectors like GPTZero, Turnitin, Originality.ai, Winston AI, and Copyleaks.</p>
                </div>
              </article>
              <article className="feature-card">
                <div className="feature-icon" aria-hidden="true">🔒</div>
                <div>
                  <h4>Retain Core Meaning</h4>
                  <p>While the vocabulary and structure shift, the core arguments, messaging, and intent of your content remain completely unchanged.</p>
                </div>
              </article>
              <article className="feature-card">
                <div className="feature-icon" aria-hidden="true">⚡</div>
                <div>
                  <h4>SEO-Friendly Output</h4>
                  <p>Natural flow mirrors helpful content guidelines, enhancing reader engagement and SEO rankings.</p>
                </div>
              </article>
              <article className="feature-card">
                <div className="feature-icon" aria-hidden="true">🛡️</div>
                <div>
                  <h4>Zero Data Storage</h4>
                  <p>We respect your privacy. Input text is processed strictly in-memory and deleted immediately after use.</p>
                </div>
              </article>
            </div>
          </section>

          {/* FAQ */}
          <section className="content-wrap section" id="faq">
            <p className="section-eyebrow">FAQ</p>
            <h2 className="section-title">Frequently asked <em>questions</em></h2>
            <div className="faq-list">
              <details>
                <summary>How does the AI Humanizer work?</summary>
                <div className="faq-answer">It restructures phrasing, varies sentence lengths, replaces overused AI vocabulary, and introduces native stylistic elements to bypass pattern detectors.</div>
              </details>
              <details>
                <summary>Will I lose the original meaning of my text?</summary>
                <div className="faq-answer">No. Our rewriting process strictly preserves the underlying facts, logical structure, and overall meaning.</div>
              </details>
              <details>
                <summary>Is this tool free to use?</summary>
                <div className="faq-answer">Yes, we offer free daily usage limits. Sign up or log in to access completely unlimited features.</div>
              </details>
            </div>
          </section>

          {/* CTA */}
          <section className="content-wrap">
            <div className="cta-banner">
              <h2 className="cta-heading">Ready to transform your AI text?</h2>
              <p>Paste your content into the Humanizer tool and click “Humanize” to get natural, polished writing in seconds.</p>
              <button className="btn-sm primary" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                Back to Tool 🚀
              </button>
            </div>
          </section>

          {/* RELATED TOOLS */}
          <section className="content-wrap section" aria-labelledby="related-tools-heading">
            <p className="section-eyebrow">Explore More Free Writing Tools</p>
            <h2 className="section-title">Explore more <em>free writing tools</em></h2>
            <nav className="related-grid" aria-label="Related tools">
              <a href="https://ook-calculator.com/AIDetector" className="related-card">
                <span aria-hidden="true">🤖</span>
                <span>AI Content Detector</span>
              </a>
              <a href="https://ook-calculator.com/Paraphrase" className="related-card">
                <span aria-hidden="true">✏️</span>
                <span>Paraphrase Tool</span>
              </a>
              <a href="https://ook-calculator.com/Humanize" className="related-card">
                <span aria-hidden="true">🧑</span>
                <span>AI Humanizer</span>
              </a>
              <a href="https://ook-calculator.com/GrammarChecker" className="related-card">
                <span aria-hidden="true">📝</span>
                <span>Grammar Checker</span>
              </a>
              <a href="https://ook-calculator.com/Summarizer" className="related-card">
                <span aria-hidden="true">📋</span>
                <span>Text Summarizer</span>
              </a>
              <a href="https://ook-calculator.com" className="related-card">
                <span aria-hidden="true">🧮</span>
                <span>All Tools</span>
              </a>
            </nav>
          </section>
        </main>

        {/* SCROLL TO TOP */}
        <section className="cse-header-top">
          <Link to="#" onClick={scrollToTop} aria-label="Scroll to top">
            <GrLinkTop />
          </Link>
        </section>

        <footer className="footer">
          <p>✨ AI Text Humanizer — Powered by NLTK WordNet & TextBlob</p>
        </footer>
      </div>
    </>
  );
}